import React, { useState, useEffect, useContext, useRef } from "react";
import { useDispatch } from "react-redux";
import { Redirect, useParams } from "react-router-dom";
import { useHistory, useLocation } from 'react-router';
import axios from "axios";
import moment from "moment";

import { Card, CardBody, Layout, Menu, Row, Col, Button, Space, message, Empty, Spin, Tag } from "antd";
import { LeftOutlined, RightOutlined, NodeIndexOutlined, RiseOutlined, DashboardOutlined, ThunderboltOutlined, HeartOutlined, CompassOutlined, StarOutlined, ClockCircleOutlined, FireOutlined } from "@ant-design/icons";

import { setBreadcrumb } from "redux/actions/app.action";
import generalService from "services/general.service";
import generalHelper from "helper/general.helper";
import ActivityService from "services/activity.service";

import Header from "components/shared/Header";
import Filter from "components/overview/Filter";
import DataTable from "components/overview/DataTable";
import emptyImg from "assets/img/empty.svg";
import AreaChart from "components/shared/AreaChart";

import { SocketContext } from "contexts/SocketContext";
import { AppContext } from "contexts/AppContext";

import {
  CANCEL_REQUEST,
  DATE_TIME_FORMAT,
  FILE_TYPE,
  STATS_TYPE,
  WS_URL,
  TOKEN_NAME,
  VIEW_TYPE,
  SPORT_TYPE,
  SPORT_ICONS
} from "constants/index";

import AuthorizedFunction from "config/AuthorizedFunction";

const Activities = (data) => {
  // const socket = useContext(SocketContext);
  // const [wsMessage, setWSMessage] = useState("");
  const appContext = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const { athlete_id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [useSearchData, setUseSearchData] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    console.log("data",data);
    if (appContext.selectedAthlete) {
      const params = {
        id: appContext.selectedAthlete.athlete_id,
        start: appContext.dateRange.startDate?.format(DATE_TIME_FORMAT.DBDATE) || null,
        end: appContext.dateRange.endDate?.format(DATE_TIME_FORMAT.DBDATE) || null,
        sport: appContext.sportView
      };

      // If search params are set, use them for filtering
      if (appContext.searchParams) {
        params.start = appContext.searchParams.startDate?.format(DATE_TIME_FORMAT.DBDATE) || null;
        params.end = appContext.searchParams.endDate?.format(DATE_TIME_FORMAT.DBDATE) || null;
        params.activity_keyword = appContext.searchParams.activity_keyword || null;
        params.activity_distance = appContext.searchParams.activity_distance || null;
        params.activity_elevation_gain = appContext.searchParams.activity_elevation_gain || null;
        setUseSearchData(true);
      } else {
        setUseSearchData(false);
      }
      appContext.setSearchParams(null); // Reset search params after use

      fetchData(params);
    } else {
      const searchParams = new URLSearchParams(location.search);
      if (searchParams) {
        const params = {
          id: athlete_id , 
          start: searchParams.get("start") || null,
          end: searchParams.get("end") || null,
          sport: searchParams.get("sport"),
        }
        appContext.setSelectedAthlete({athlete_id: athlete_id, dateRange: {startDate: moment(searchParams.get("start")), endDate: moment(searchParams.get("end"))}});
        appContext.setDateRange({ startDate: moment(searchParams.get("start")), endDate: moment(searchParams.get("end"))});
        appContext.setSportView(searchParams.get("sport"));
        appContext.setPageView(searchParams.get("view"));
        fetchData(params);
      }
    }
  }, [data, appContext.selectedAthlete?.athlete_id, appContext.pageView, appContext.dateRange, appContext.sportView, appContext.callSearch]);

  const fetchData = (params) => {
    setIsLoading(true);
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    ActivityService.getListItems(params, source).then(
      (request) => {
        setTableData(request.activities);
        setChartData(request.chartData);
        setTimeout(() => {
          const userStats = {
            distance: generalHelper.calculateTotalDistance(request.activities),
            gain: generalHelper.calculateTotalGain(request.activities),
            activities: request.activities.length
          }
          appContext.setSelectedAthlete({...request.athlete, dateRange: {startDate: moment(params.start) || null, endDate: moment(params.end) || null}, stats: userStats});
          setIsLoading(false);
        }, 200);
      },
      (error) => {
        if (error && error.response.data && error.response.data.errors) {
          message.error(error.response.data.errors.join(". "), 5);
        } else if (error && error.message !== CANCEL_REQUEST) {
          message.error("API got error, please contact us.", 5);
        }
        console.log(error);
        setIsLoading(false);
        return Promise.reject();
      }
    );
  };

  const Activity = (data) => {
    return (
      <>
        <Space
          key={data.id}
          direction="vertical"
          size={16}
          style={{ width: "100%" }}
        >
          <a href={`https://www.strava.com/activities/${data.id}`}>
            <Card
              bordered={true}
              className={appContext.sportView !== SPORT_TYPE.All ? data.admin_flagged ? "activity-item flagged" : data.commute ? "activity-item commute" : "activity-item" : "activity-item"}
            >
              <h4><a href={`https://www.strava.com/activities/${data.id}`}>{data.name}</a> - {useSearchData ? moment(data.start_date).format(DATE_TIME_FORMAT.WEEKDAYTIMEFULL) : appContext.pageView === VIEW_TYPE.Year ? moment(data.start_date).format(DATE_TIME_FORMAT.WEEKDAYTIMELONG) : moment(data.start_date).format(DATE_TIME_FORMAT.WEEKDAYTIME)}</h4>
              { appContext.sportView !== SPORT_TYPE.All &&
                <div className="item-content">
                  { data.admin_flagged && <div className="stats-header">{data.reflagged ? <Tag color="red">{data.reflagged_remark}</Tag> : generalHelper.formatFlagged(data.admin_flagged_remark, false)}</div> }
                  { !data.admin_flagged && data.reflagged && <div className="stats-header">{generalHelper.formatFlagged(data.admin_flagged_remark, false)}<Tag color="blue">{data.reflagged_remark}</Tag></div> }
                  <div className="stats-header">Distance: <span className="stats-value">{generalHelper.formatDistance(data.distance, 2, true)}</span>{generalHelper.markVirtualSport(data.sport_type, false)}</div>
                  <div className="stats-header">Gain: <span className="stats-value">{generalHelper.formatDisplayNumber(data.total_elevation_gain, 0, STATS_TYPE.Elevation, false, true, false)}</span>{generalHelper.calculateIncline(data.total_elevation_gain, data.distance, true, true, true)}</div>
                  <div className="stats-header">Time: <span className="stats-value">{generalHelper.formatDisplayTime(data.moving_time)} ({generalHelper.formatDisplayTime(data.elapsed_time)})</span></div>
                  <div className="stats-panel">
                    {generalHelper.formatDisplayNumber([data.average_heartrate, data.max_heartrate], 0, STATS_TYPE.Heartrate, true, true, true)}
                    {generalHelper.calculatePace([{metters: data.distance, seconds: data.moving_time}, {metters: data.max_speed, seconds: 1}], appContext.sportView, true, true, false)}
                    {generalHelper.formatDisplayNumber([data.average_watts, data.max_watts], 0, STATS_TYPE.Power, true, true, data.device_watts)}
                  </div>
                  { data.calories || data.average_temp || data.average_cadence ?
                  <div className="stats-panel">
                    {generalHelper.formatDisplayNumber(data.calories, 0, STATS_TYPE.Calories, true, true, true)}
                    {generalHelper.formatDisplayNumber(data.average_temp, 0, STATS_TYPE.Temp, true, true, false)}
                    {generalHelper.formatCadence(data.average_cadence, 0, appContext.sportView, true, true, false)}
                  </div>
                  : ""}
                </div>
              }
              { appContext.sportView === SPORT_TYPE.All &&
                <div className="item-content">
                  <div className="stats-header">Distance: <span className="stats-value">{generalHelper.formatDistance(data.distance, 2, true)}</span>{generalHelper.markVirtualSport(data.sport_type, false)}</div>
                  <div className="stats-header">Gain: <span className="stats-value">{generalHelper.formatDisplayNumber(data.total_elevation_gain, 0, STATS_TYPE.Elevation, false, true, false)}</span>{generalHelper.calculateIncline(data.total_elevation_gain, data.distance, true, true, true)}</div>
                  <div className="stats-header">Time: <span className="stats-value">{generalHelper.formatDisplayTime(data.moving_time)} ({generalHelper.formatDisplayTime(data.elapsed_time)})</span></div>
                  <div className="stats-panel">
                    {generalHelper.formatDisplayNumber([data.average_heartrate, data.max_heartrate], 0, STATS_TYPE.Heartrate, true, true, true)}
                    {generalHelper.formatDisplayNumber(data.calories, 0, STATS_TYPE.Calories, true, true, true)}
                    {generalHelper.formatDisplayNumber(data.average_temp, 0, STATS_TYPE.Temp, true, true, false)}
                  </div>
                </div>
              }
            </Card>
          </a>
        </Space>
      </>
    );
  };

  return (
    <>
      <Space direction="vertical" size={16} style={{ width: "100%" }}>
        <Row gutter={[32, 24]}>
          <Col flex="auto" sm={24}>
          {!isLoading ? (
            <>
              { chartData && chartData.length > 0 &&
                  <AreaChart
                    css={null}
                    chartTitle={null}
                    chartData={chartData}
                    useSearchData={useSearchData}
                  />
                }
                {tableData && tableData.length > 0 ? tableData.map((s) => Activity(s)) : (
                  <Empty image={emptyImg} imageStyle={{height: 60, marginTop: 60}} description={<span>No data</span>}></Empty>
                )}
            </>
          ) : (
            <Empty image={emptyImg} imageStyle={{height: 60}} description={<span>Loading..</span>}>
              <Spin />
            </Empty>
          )}
          </Col>
        </Row>
      </Space>
    </>
  );
};

export default Activities;
